import Swiper from 'swiper';
import { Autoplay } from 'swiper/modules';
import { animate, utils } from 'animejs';

class MainHoverSlider {
	constructor(container) {
		this.container = container;
		this.slider = container.querySelector('.main-hover-slider__slider');
		this.sliderInner = container.querySelector('.main-hover-slider__slider-inner');
		this.leftPanel = container.querySelector('.main-hover-slider__left-panel');
		this.rightPanel = container.querySelector('.main-hover-slider__right-panel');
		this.infoBlocks = Array.from(this.rightPanel.querySelectorAll('.main-hover-slider__info'));

		this.addCart();
		this.initSlider();
		this.addPanelsHoverEvents();
		this.initCustomCursor();
		this.initVisibilityWatcher();
		this.initClickableWatcher();
		window.addEventListener('resize', () => this.updateSliderAndHandlers());
	}

	addCart() {
		const cartButton = this.container.querySelectorAll('[data-cart-button]');
		const productCounter = this.container.querySelectorAll('[data-cart-counter]');

		if (!cartButton || !productCounter) return;

		cartButton.forEach((button, n) => {
			button.addEventListener('click', () => {
				productCounter[n].classList.add('_active');
				button.classList.add('_active');
			});
		});
	}

	initCustomCursor() {
		this.cursor = document.createElement('div');
		this.cursor.className = 'main-hover-slider__custom-cursor';
		this.cursor.innerHTML = `
			<svg class="main-hover-slider__cursor-icon" xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32" fill="none">
				<path d="" fill="white"/>
			</svg>
		`;
		document.body.appendChild(this.cursor);

		this.cursorIcon = this.cursor.querySelector('path');
		this.cursorX = window.innerWidth / 2;
		this.cursorY = window.innerHeight / 2;
		this.targetX = this.cursorX;
		this.targetY = this.cursorY;
		this.lerpFactor = 1;
		this.activeCursor = false;

		document.addEventListener('mousemove', (e) => {
			this.targetX = e.clientX;
			this.targetY = e.clientY;
		});

		const animateCursor = () => {
			if (this.activeCursor) {
				this.cursorX += (this.targetX - this.cursorX) * this.lerpFactor;
				this.cursorY += (this.targetY - this.cursorY) * this.lerpFactor;
				this.cursor.style.transform = `translate3d(${this.cursorX}px, ${this.cursorY}px, 0)`;
			}
			requestAnimationFrame(animateCursor);
		};
		animateCursor();

		this.updateCursorHandlers();
	}

	setCursor(type) {
		if (!this.cursor) return;

		const paths = {
			left: 'M25.3301 13.3359H30.6641V18.6699H25.3301V30.6699L1.33008 20.0029V12.0029L25.3301 1.33594V13.3359Z',
			right: 'M30.6641 12.0029V20.0029L6.66406 30.6699V18.6699H1.33008V13.3359H6.66406V1.33594L30.6641 12.0029Z',
		};

		if (type && window.innerWidth >= 769) {
			this.activeCursor = true;
			this.cursor.classList.add('visible');
			this.cursorIcon.setAttribute('d', paths[type]);
			document.body.style.cursor = 'none';
		} else {
			this.activeCursor = false;
			this.cursor.classList.remove('visible');
			document.body.style.cursor = '';
		}
	}

	updateCursorHandlers() {
		const isDesktop = window.innerWidth >= 769;

		this.leftPanel?.removeEventListener('mouseenter', this._cursorLeftEnter);
		this.rightPanel?.removeEventListener('mouseenter', this._cursorRightEnter);
		this.slider?.removeEventListener('mouseenter', this._cursorLeave);
		this.leftPanel?.removeEventListener('mouseleave', this._cursorLeave);
		this.rightPanel?.removeEventListener('mouseleave', this._cursorLeave);
		this.slider?.removeEventListener('mouseleave', this._cursorLeave);

		if (!isDesktop) {
			this.setCursor(null);
			return;
		}

		this._cursorLeftEnter = () => this.setCursor('left');
		this._cursorRightEnter = () => this.setCursor('right');
		this._cursorLeave = () => this.setCursor(null);

		this.leftPanel.addEventListener('mouseenter', this._cursorLeftEnter);
		this.rightPanel.addEventListener('mouseenter', this._cursorRightEnter);
		this.slider?.addEventListener('mouseenter', this._cursorLeave);
		this.leftPanel.addEventListener('mouseleave', this._cursorLeave);
		this.rightPanel.addEventListener('mouseleave', this._cursorLeave);
		this.slider?.addEventListener('mouseleave', this._cursorLeave);
	}

	initVisibilityWatcher() {
		const observer = new IntersectionObserver(
			(entries) => {
				entries.forEach((entry) => {
					if (!entry.isIntersecting) {
						this.setCursor(null);
					}
				});
			},
			{
				root: null,
				threshold: 0,
			},
		);

		observer.observe(this.container);
		this.visibilityObserver = observer;

		let ticking = false;

		window.addEventListener('scroll', () => {
			if (ticking) return;
			ticking = true;

			requestAnimationFrame(() => {
				ticking = false;
				if (!this.container || window.innerWidth < 769) return;

				const rect = this.container.getBoundingClientRect();
				const inside =
					this.targetX >= rect.left &&
					this.targetX <= rect.right &&
					this.targetY >= rect.top &&
					this.targetY <= rect.bottom;

				if (!inside) {
					if (this.activeCursor) this.setCursor(null);
					return;
				}

				if (!this.activeCursor) {
					const leftRect = this.leftPanel.getBoundingClientRect();
					const rightRect = this.rightPanel.getBoundingClientRect();

					if (
						this.targetX >= leftRect.left &&
						this.targetX <= leftRect.right &&
						this.targetY >= leftRect.top &&
						this.targetY <= leftRect.bottom
					) {
						this.setCursor('left');
					} else if (
						this.targetX >= rightRect.left &&
						this.targetX <= rightRect.right &&
						this.targetY >= rightRect.top &&
						this.targetY <= rightRect.bottom
					) {
						this.setCursor('right');
					}
				}
			});
		});
	}

	initClickableWatcher() {
		const setup = () => {
			const isDesktop = window.innerWidth >= 769;

			if (isDesktop) {
				this.leftPanel?.removeEventListener('click', this._leftClick);
				this.rightPanel?.removeEventListener('click', this._rightClick);

				this._leftClick = (e) => {
					if (
						e.target.closest(
							'a, button:not(.main-hover-slider__cart-counter button), [role="button"], .main-hover-slider__cart-counter',
						)
					)
						return;
					this.swiper.slidePrev();
				};
				this._rightClick = (e) => {
					if (
						e.target.closest(
							'a, button:not(.main-hover-slider__cart-counter button), [role="button"], .main-hover-slider__cart-counter',
						)
					)
						return;
					this.swiper.slideNext();
				};

				this.leftPanel?.addEventListener('click', this._leftClick);
				this.rightPanel?.addEventListener('click', this._rightClick);
			} else {
				this.leftPanel?.removeEventListener('click', this._leftClick);
				this.rightPanel?.removeEventListener('click', this._rightClick);
			}

			if (this.clickableElements) {
				this.clickableElements.forEach((el) => {
					el.removeEventListener('mouseenter', this.clickableEnter);
					el.removeEventListener('mouseleave', this.clickableLeave);
				});
			}

			if (!isDesktop) {
				this.clickableElements = null;
				return;
			}

			const clickableSelectors =
				'a, button:not(.main-hover-slider__cart-counter button), [role="button"], .main-hover-slider__cart-counter';
			this.clickableElements = this.container?.querySelectorAll(clickableSelectors);

			this.clickableEnter = () => {
				this.cursorWasActive = this.activeCursor;
				this.setCursor(null);
				if (this.swiper?.autoplay.running) {
					this.swiper.autoplay.stop();
					this.autoplayWasRunning = true;
				}
			};

			this.clickableLeave = (e) => {
				const insideContainer = this.container.contains(e.relatedTarget);

				if (this.autoplayWasRunning) {
					this.swiper.autoplay.start();
					this.autoplayWasRunning = false;
				}

				if (insideContainer && this.cursorWasActive) {
					if (this.leftPanel.matches(':hover')) {
						this.setCursor('left');
					} else if (this.rightPanel.matches(':hover')) {
						this.setCursor('right');
					}
				}
			};

			this.clickableElements.forEach((el) => {
				el.addEventListener('mouseenter', this.clickableEnter);
				el.addEventListener('mouseleave', this.clickableLeave);
			});
		};

		setup();

		this.resizeHandler = () => setup();

		window.addEventListener('resize', this.resizeHandler);
	}

	calcSpaceBetween() {
		this.sw = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--scrollbar-width'));
		const vw = window.innerWidth - this.sw;
		this.size = parseInt(getComputedStyle(this.slider).getPropertyValue('--size'));
		const rem = parseFloat(getComputedStyle(document.documentElement).fontSize);
		let sb = (vw - this.size * rem * 3) / 2;
		if (vw < 1025 && vw >= 769) {
			sb = (vw - this.size * rem * 2.8) / 2;
		} else if (vw < 769) {
			sb = (vw - this.size * rem * 1.25) / 2;
		}
		return sb;
	}

	initSlider() {
		this.swiper = new Swiper(this.slider, {
			modules: [Autoplay],
			speed: 500,
			slidesPerView: 'auto',
			simulateTouch: false,
			centeredSlides: true,
			spaceBetween: this.calcSpaceBetween(),
			loop: true,
			autoplay: {
				delay: 2500,
				disableOnInteraction: false,
			},
			breakpoints: {
				769: {
					allowTouchMove: false,
					simulateTouch: false,
					autoplay: {
						delay: 2500,
						disableOnInteraction: false,
					},
				},
				0: {
					allowTouchMove: true,
					simulateTouch: true,
					autoplay: {
						delay: 1500,
						disableOnInteraction: false,
					},
				},
			},
			on: {
				slideChange: () => this.updateInfo(),
			},
		});

		this.slider.style.opacity = 1;

		this.updateInfo();
	}

	updateInfo(init = false) {
		if (!this.swiper || typeof this.swiper.realIndex !== 'number') return;

		const realIndex = this.swiper.realIndex;
		if (realIndex === this.prevIndex && !init) return;
		this.prevIndex = realIndex;

		this.infoBlocks.forEach((block, i) => {
			utils.remove(block);

			if (i === realIndex) {
				if (init) {
					block.style.opacity = 1;
					block.style.transform = 'translateY(0px)';
					block.style.pointerEvents = 'all';
				} else {
					animate(block, {
						opacity: [0, 1],
						translateY: [20, 0],
						pointerEvents: 'all',
						duration: 300,
						delay: 300,
						ease: 'outQuad',
					});
				}
			} else {
				animate(block, {
					opacity: 0,
					translateY: -20,
					pointerEvents: 'none',
					duration: 300,
					ease: 'outQuad',
				});
			}
		});
	}

	addPanelsHoverEvents() {
		this.hoverHandler = (reverse) => {
			this.swiper.params.autoplay.reverseDirection = reverse;
			this.swiper.params.autoplay.delay = 1500;
			this.swiper.autoplay.stop();
			this.swiper.autoplay.start();
		};

		this.leaveHandler = () => {
			this.swiper.params.autoplay.reverseDirection = false;
			this.swiper.params.autoplay.delay = 2500;
			this.swiper.autoplay.stop();
			this.swiper.autoplay.start();
		};

		this.updateHoverHandlers();
	}

	updateHoverHandlers() {
		const isDesktop = window.innerWidth >= 769;

		this.leftPanel.removeEventListener('mouseenter', this._leftEnter);
		this.rightPanel.removeEventListener('mouseenter', this._rightEnter);
		this.leftPanel.removeEventListener('mouseleave', this.leaveHandler);
		this.rightPanel.removeEventListener('mouseleave', this.leaveHandler);

		this.slider.removeEventListener('touchstart', this._touchStart);
		this.slider.removeEventListener('touchend', this._touchEnd);
		this.slider.removeEventListener('touchcancel', this._touchEnd);

		if (isDesktop) {
			this._leftEnter = () => this.hoverHandler(true);
			this._rightEnter = () => this.hoverHandler(false);

			this.leftPanel.addEventListener('mouseenter', this._leftEnter);
			this.rightPanel.addEventListener('mouseenter', this._rightEnter);
			this.leftPanel.addEventListener('mouseleave', this.leaveHandler);
			this.rightPanel.addEventListener('mouseleave', this.leaveHandler);
		} else {
			this._touchStart = () => this.swiper.autoplay.stop();
			this._touchEnd = () => this.swiper.autoplay.start();

			this.slider.addEventListener('touchstart', this._touchStart);
			this.slider.addEventListener('touchend', this._touchEnd);
			this.slider.addEventListener('touchcancel', this._touchEnd);
		}
	}

	updateSlider() {
		if (this.swiper) {
			this.swiper.params.spaceBetween = this.calcSpaceBetween();
			this.swiper.update();
		}
	}

	pauseForCartButtonOnSlider() {
		const isDesktop = window.innerWidth >= 769;

		if (!isDesktop) {
			const cartButtons = this.container.querySelectorAll(
				'.main-hover-slider__banner-button, .main-hover-slider__cart-counter button',
			);

			cartButtons.forEach((button) => {
				button.addEventListener('click', () => {
					this.swiper.autoplay.stop();

					setTimeout(() => {
						this.swiper.autoplay.start();
					}, 2500);
				});
			});
		}
	}

	updateSliderAndHandlers() {
		this.updateSlider();
		this.updateHoverHandlers();
		this.updateCursorHandlers();
		this.pauseForCartButtonOnSlider();
	}
}

document.querySelectorAll('[data-main-hover-slider]').forEach((el) => new MainHoverSlider(el));
export default MainHoverSlider;
